import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import ShieldCheckIcon from './icons/ShieldCheckIcon';
import WhistleIcon from './icons/WhistleIcon';

interface VerificationData {
  orderNumber: string;
  name: string;
  position: string;
  administrativeHeadquarters: string;
  missionLocation: string;
  departureDate: string;
  returnDate: string;
  missionTiming: string;
  missionType: string;
  createdAt: string;
}

const VerificationPage: React.FC = () => {
  const [data, setData] = useState<VerificationData | null>(null);
  const [multipleData, setMultipleData] = useState<VerificationData[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchVerificationData = async () => {
      // Support both hash-based (#/verify/123) and regular path (/verify/123) URLs
      let verificationId = '';
      
      if (window.location.hash.startsWith('#/verify/')) {
        // Hash-based routing: #/verify/some-id
        const hash = window.location.hash;
        const pathParts = hash.substring(1).split('/');
        verificationId = pathParts[pathParts.length - 1];
      } else if (window.location.pathname.startsWith('/verify/')) {
        // Regular path routing: /verify/some-id
        const pathParts = window.location.pathname.split('/');
        verificationId = pathParts[pathParts.length - 1];
      }

      if (!verificationId) {
        setError("معرف التحقق مفقود في الرابط.");
        setLoading(false);
        return;
      }

      try {
        // Call the secure RPC function
        const { data: orderData, error: dbError } = await supabase
          .rpc('verify_mission_order', { verification_id: verificationId });

        if (dbError) {
          throw new Error(dbError.message);
        }

        if (!orderData || orderData.length === 0) {
          throw new Error("أمر المهمة غير موجود أو غير صالح.");
        }

        const mappedData = orderData.map((item: any) => {
          const snapshot = item.data_snapshot;
          return {
            orderNumber: snapshot.orderNumber,
            name: snapshot.name,
            position: snapshot.position,
            administrativeHeadquarters: snapshot.administrativeHeadquarters,
            missionLocation: snapshot.missionLocation,
            departureDate: snapshot.departureDate,
            returnDate: snapshot.returnDate,
            missionTiming: snapshot.missionTiming,
            missionType: snapshot.missionType,
            createdAt: item.created_at,
          };
        });

        if (mappedData.length === 1) {
          setData(mappedData[0]);
        } else {
          setMultipleData(mappedData);
        }

      } catch (e: any) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };

    fetchVerificationData();
  }, []);

  const formatArabicDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ar-DZ', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const formatArabicDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('ar-DZ', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleSelect = (selectedData: VerificationData) => {
    setData(selectedData);
    setMultipleData(null);
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500 mx-auto mb-2"></div>
          <p className="text-lg text-white/80">جاري التحقق...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="text-center bg-red-900/50 border-2 border-red-500 rounded-lg p-4">
          <ShieldCheckIcon className="h-16 w-16 text-rose-400 mx-auto mb-2" />
          <h2 className="text-2xl font-bold text-red-300">فشل التحقق</h2>
          <p className="mt-2 text-red-200">{error}</p>
        </div>
      );
    }

    if (multipleData) {
      return (
        <div className="space-y-4">
          <div className="text-center mb-4">
            <h2 className="text-xl font-bold text-white">تم العثور على عدة أوامر</h2>
            <p className="text-white/60">الرجاء اختيار الأمر الصحيح من القائمة أدناه</p>
          </div>
          {multipleData.map((item, index) => (
            <div 
              key={index}
              onClick={() => handleSelect(item)}
              className="bg-white/10 border border-white/20 rounded-lg p-4 cursor-pointer hover:bg-white/20 transition-colors"
            >
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-bold text-white">{item.name}</p>
                  <p className="text-sm text-white/70">{item.missionType}</p>
                </div>
                <div className="text-start">
                  <p className="text-sm text-white/70">{formatArabicDate(item.departureDate)}</p>
                  <p className="text-xs text-white/50">{formatArabicDateTime(item.createdAt)}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      );
    }

    if (data) {
      return (
        <div className="bg-green-900/50 border-2 border-green-500 rounded-lg p-4">
          <div className="text-center">
            <ShieldCheckIcon className="h-16 w-16 text-green-300 mx-auto mb-2" />
            <h2 className="text-2xl font-bold text-green-200">وثيقة أصلية</h2>
            <p className="mt-2 text-green-200">
              تم إنشاء أمر المهمة هذا بواسطة نظامنا في {formatArabicDateTime(data.createdAt)}.
            </p>
          </div>
          <div className="mt-2 border-t border-green-400/50 pt-6 space-y-2">
            <DetailRow label="رقم الأمر" value={`${data.orderNumber}`} />
            <DetailRow label="الاسم" value={data.name} />
            <DetailRow label="الوظيفة" value={data.position} />
            <DetailRow label="نوع المهمة" value={data.missionType} />
            <DetailRow label="مكان المهمة" value={data.missionLocation} />
            <DetailRow label="التاريخ" value={formatArabicDate(data.departureDate)} />
          </div>
        </div>
      );
    }

    return null;
  };
  
  const DetailRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
    <div className="grid grid-cols-3 gap-2" dir="rtl">
        <dt className="text-sm font-medium text-green-200/80 text-end">{label}</dt>
        <dd className="text-sm text-white col-span-2 text-end">{value}</dd>
    </div>
  );

  return (
    <div className="min-h-screen bg-white/5 text-white flex items-center justify-center p-4" dir="rtl">
      <div className="w-full max-w-2xl">
        <div className="flex flex-col items-center space-y-3 mb-2">
          <WhistleIcon className="h-12 w-12 text-primary-500" />
          <h1 className="text-3xl font-bold text-white">التحقق من أمر المهمة</h1>
        </div>
        {renderContent()}
      </div>
    </div>
  );
};

export default VerificationPage;